<template>
  <div class="whiteboard">
    <canvas
      :class="['drawing-area', whiteboardTool]"
      ref="drawingArea"
      @mousedown="handleDown"
      @mouseup="handleUp"
      @mouseout="handleOut"
      @mousemove="handleMove"
      @touchstart.prevent="(e) => handleDown(e, true)"
      @touchmove.prevent="(e) => handleMove(e, true)"
      @touchend.prevent="(e) => handleUp(e, true)"
      :width="canvasSize.width"
      :height="canvasSize.height"
    />
    <video
      :srcObject.prop="projector.stream"
      class="projector"
      ref="projector"
      autoplay
      muted
      playsinline
    />
    <audio v-if="!projector.isStreamOwner" :srcObject.prop="projector.stream" autoplay/>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { WHITEBOARD_BASE_SIZE } from '@/lib/constants'

export default {
  name: 'Whiteboard',
  props: {
    enabledWhiteboard: Boolean
  },
  computed: {
    canvasSize: () => {
      return {
        width: WHITEBOARD_BASE_SIZE.width,
        height: WHITEBOARD_BASE_SIZE.height
      }
    },
    ...mapGetters({
      isDrawing: 'whiteboard/isDrawing',
      whiteboardTool: 'whiteboard/tool',
      drawingPosition: 'whiteboard/position',
      receivedData: 'whiteboard/receivedData',
      projector: 'projector/projector'
    })
  },
  methods: {
    eventPosition: function(e, touch) {
      let x, y

      if (touch) {
        const canvasRect = this.$refs.drawingArea.getBoundingClientRect()
        x = e.changedTouches[0].pageX - canvasRect.left
        y = e.changedTouches[0].pageY - canvasRect.top
      } else {
        x = e.offsetX
        y = e.offsetY
      }

      return {
        x: x,
        y: y
      }
    },
    handleDown: function(e, touch) {
      if (e.button == 2) return
      const { x, y } = this.eventPosition(e, touch)

      this.setDrawing({
        isDrawing: true,
        x: x,
        y: y
      })
    },
    handleUp: function(e) {
      if (e.button == 2) return
      this.setDrawing({ isDrawing: false })
    },
    handleOut: function() {
      if (this.isDrawing) {
        this.setDrawing({ isDrawing: false })
      }
    },
    handleMove: function(e, touch) {
      if (!this.isDrawing) return
      const { x, y } = this.eventPosition(e, touch)

      this.setNewPosition({
        x: x,
        y: y
      })
      this.toolAction()
    },
    adjustMagnification: function() {
      this.setMagnification({
        width: this.$refs.drawingArea.clientWidth,
        height: this.$refs.drawingArea.clientHeight
      })
    },
    ...mapMutations({
      setContext: 'whiteboard/setContext',
      setMagnification: 'whiteboard/setMagnification',
      setDrawing: 'whiteboard/setDrawing',
      setNewPosition: 'whiteboard/setNewPosition'
    }),
    ...mapActions({
      toolAction: 'whiteboard/toolAction',
      remoteAction: 'whiteboard/remoteAction'
    })
  },
  mounted() {
    this.setContext(this.$refs.drawingArea.getContext('2d'))
    window.addEventListener('resize', this.adjustMagnification)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustMagnification)
  },
  watch: {
    enabledWhiteboard: function(val) {
      if (val) this.adjustMagnification()
    },
    receivedData: function(data) {
      this.remoteAction(data)
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.whiteboard {
  position: relative;
  width: 90%;
  background-color: rgba(0, 0, 0, 0.6);
  box-shadow: 0 5px 12px 2px rgba(17, 17, 17, 0.5);

  &:before {
    content: "";
    display: block;
    padding-top: 56.25%;
  }

  .drawing-area, .projector {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .drawing-area {
    z-index: 2;

    &.eraser {
      cursor: url('/cursor/eraser.png') 1 15, crosshair;
    }

    &.pencil {
      cursor: url('/cursor/pen.png') 1 15, crosshair;
    }
  }

  .projector {
  }
}

</style>
